import React from 'react';
import { IWIPTask, WIP_PRIORITY_COLORS } from '@/types/psi';

interface WIPTaskCardProps {
  task: IWIPTask;
  onClick?: (task: IWIPTask) => void;
}

const WIPTaskCard: React.FC<WIPTaskCardProps> = ({ task, onClick }) => {
  const priorityClass = WIP_PRIORITY_COLORS[task.priority];

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('zh-TW', { month: 'short', day: 'numeric' });
  };

  const formatAmount = (amount?: number) => {
    if (amount === undefined) return null;
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div
      onClick={() => onClick?.(task)}
      className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-3 cursor-pointer hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200"
    >
      {/* Header: Title + Priority */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2">
          {task.title}
        </h4>
        <span className={`px-2 py-0.5 text-xs font-medium rounded-full shrink-0 ${priorityClass}`}>
          {task.priority}
        </span>
      </div>

      {/* Reference Number */}
      {task.reference && (
        <div className="flex items-center gap-1 mb-2">
          <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
          </svg>
          <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
            {task.reference}
          </span>
        </div>
      )}

      {/* Description */}
      {task.description && (
        <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
          {task.description}
        </p>
      )}

      {/* Footer: Amount, Due Date, Assignee */}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2">
          {/* Amount */}
          {task.amount !== undefined && (
            <span className="text-xs font-medium text-green-600 dark:text-green-400">
              {formatAmount(task.amount)}
            </span>
          )}

          {/* Due Date */}
          {task.dueDate && (
            <div className="flex items-center gap-1">
              <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {formatDate(task.dueDate)}
              </span>
            </div>
          )}
        </div>

        {/* Assignee */}
        {task.assignee && (
          <div className="flex items-center gap-1">
            <div className="w-5 h-5 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center">
              <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400">
                {task.assignee.charAt(0).toUpperCase()}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WIPTaskCard;
